import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/motion";
import { CONTACT_EMAIL, COMPANY, PRICING } from "@/config/site";
import { usePageMeta } from "@/lib/meta";

const Shell = ({ title, updated, children, testId }) => (
  <div data-testid={testId}>
    <section className="bg-coacher-navy pt-32 pb-16 sm:pt-40 sm:pb-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-8">
        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
          <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
          Legal
        </p>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight text-white">{title}</h1>
        <p className="mt-3 text-sm text-white/60">Last updated: {updated}</p>
      </div>
    </section>
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-8">
        <Reveal>{children}</Reveal>
        <Link to="/" data-testid={`${testId}-back-link`} className="mt-14 inline-flex items-center gap-2 text-sm font-semibold text-coacher-navy underline decoration-coacher-red decoration-2 underline-offset-8 hover:text-coacher-red">
          <ArrowLeft size={15} /> Back to home
        </Link>
      </div>
    </section>
  </div>
);

const H = ({ children }) => (
  <h2 className="mt-10 font-display text-xl font-semibold uppercase tracking-wide text-coacher-navy">{children}</h2>
);
const P = ({ children }) => <p className="mt-3 text-base leading-relaxed text-coacher-slate">{children}</p>;
const Li = ({ children }) => (
  <li className="mt-2 flex items-start gap-3 text-base leading-relaxed text-coacher-slate">
    <span className="mt-2.5 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-coacher-red" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

export const Privacy = () => {
  usePageMeta("Privacy Policy | CoachER", "How CoachER collects, uses, and protects your information.", "/privacy");
  return (
    <Shell title="Privacy Policy" updated="September 2026" testId="privacy-page">
      <P>
        CoachER ("CoachER," "we," "us") is developed and operated by {COMPANY.name}, based in {COMPANY.location}. This Privacy Policy explains what information we collect through trycoacher.com (the "Site") and the CoachER mobile application (the "App" — together, the "Service"), how we use it, and the choices you have. By using the Service, you agree to this policy.
      </P>

      <H>Information we collect</H>
      <ul>
        <Li><strong className="text-coacher-navy">Launch list.</strong> If you join our launch notification list on the Site, we collect your email address and platform preference (iOS, Android, or both) so we can notify you when CoachER is available.</Li>
        <Li><strong className="text-coacher-navy">Account information.</strong> If you create a CoachER account in the App, we collect the details you provide, such as your name, email address, and coaching role (Head Coach or Assistant Coach).</Li>
        <Li><strong className="text-coacher-navy">Coaching content.</strong> We store the information you enter to run your teams — rosters, practice plans, lineups, batting orders, game and practice notes, and player development notes.</Li>
        <Li><strong className="text-coacher-navy">Usage data.</strong> We may collect standard technical information (device type, app version, pages or screens visited, approximate region) to operate, secure, and improve the Service.</Li>
      </ul>

      <H>How we use information</H>
      <ul>
        <Li>Provide and operate the Service, including syncing your teams across devices and staff members you authorize</Li>
        <Li>Send launch notifications and important service updates you asked for</Li>
        <Li>Respond to support requests sent to {CONTACT_EMAIL}</Li>
        <Li>Improve features, fix issues, and keep the Service secure</Li>
      </ul>
      <P>We do not sell your personal information. We do not use your coaching content for advertising.</P>

      <H>Player information and children</H>
      <P>
        CoachER is designed for adult coaches, not for children. The App is not directed at children under 13, and we do not knowingly collect personal information directly from children. Coaches may enter limited player details (such as first names, jersey numbers, and positions) solely to organize their teams. If you believe a child has provided us personal information directly, contact us and we will delete it.
      </P>

      <H>Subscriptions and payments</H>
      <P>
        Paid subscriptions ({PRICING.monthly.display}/month or {PRICING.annual.display}/year) are purchased through the Apple App Store or Google Play and processed by those platforms together with our subscription infrastructure provider. They process your payment under their own privacy policies. We receive only what is needed to activate and manage your subscription — we never see or store your full payment card details.
      </P>

      <H>Who we share information with</H>
      <P>We share information only with service providers who help us operate CoachER, and only as needed:</P>
      <ul>
        <Li>Cloud hosting and database providers that store the Service's data</Li>
        <Li>App store platforms and our subscription infrastructure provider, for purchases and subscription status</Li>
        <Li>Email delivery providers, if we send you launch or service emails</Li>
      </ul>
      <P>We may also disclose information if required by law or to protect the rights and safety of our users.</P>

      <H>Data retention and security</H>
      <P>
        We keep your information for as long as your account is active or as needed to provide the Service. Inactive teams remain available to you as archived seasons rather than being deleted. We use reasonable administrative and technical safeguards, but no system is perfectly secure — please use a strong password and keep it private.
      </P>

      <H>Your choices and rights</H>
      <ul>
        <Li>Update or correct your account information in the App</Li>
        <Li>Ask us to remove your email from the launch list at any time</Li>
        <Li>Request a copy or deletion of your personal information by emailing us</Li>
        <Li>Manage or cancel your subscription through the app store where you purchased it</Li>
      </ul>

      <H>Changes to this policy</H>
      <P>If we make material changes, we will update the "Last updated" date above and, where appropriate, notify you by email or in the App.</P>

      <H>Contact</H>
      <P>
        Privacy questions or requests? Email us at <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-coacher-navy underline decoration-coacher-red underline-offset-4">{CONTACT_EMAIL}</a> or write to {COMPANY.name}, {COMPANY.location}.
      </P>
    </Shell>
  );
};

export const Terms = () => {
  usePageMeta("Terms of Service | CoachER", "The terms that govern use of trycoacher.com and the CoachER app.", "/terms");
  return (
    <Shell title="Terms of Service" updated="September 2026" testId="terms-page">
      <P>
        These Terms of Service ("Terms") govern your use of trycoacher.com (the "Site") and the CoachER mobile application (the "App" — together, the "Service"), operated by {COMPANY.name} ("CoachER," "we," "us"), based in {COMPANY.location}. By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </P>

      <H>The service</H>
      <P>
        CoachER is a digital dugout assistant for youth baseball and softball coaches: practice planning, lineups, batting orders, rosters, notes, and player development tools. Features may be added, changed, or removed as we improve the product.
      </P>

      <H>Accounts and eligibility</H>
      <P>
        You must be at least 18 years old (or the age of majority where you live) to create an account. The Service is intended for coaches and team staff, not for children. You are responsible for keeping your login credentials confidential and for activity under your account.
      </P>

      <H>Free trial and paid subscriptions</H>
      <P>
        CoachER offers a free trial followed by a paid subscription: {PRICING.monthly.display} per month or {PRICING.annual.display} per year. There is no permanent free plan. Trial limits — including the number of players you can add (currently up to {PRICING.trial.playerLimit}) — are shown in the App and may be enforced there. When you exceed a trial limit, you will be prompted to subscribe.
      </P>
      <P>
        Billing, renewal, and cancellation are handled by the platform where you subscribed (Apple App Store or Google Play) under that platform's policies. Subscriptions renew automatically unless canceled through your app store account settings before the renewal date. We do not process refunds directly; refund requests follow the app store's policies.
      </P>

      <H>Coaching staff access</H>
      <P>
        The Head Coach who maintains a team's subscription may authorize Assistant Coaches to participate on that team at no additional subscription cost, with access based on the role and permissions granted. Assistant Coach access applies only to that team — an Assistant Coach who wants to create and own an independent team needs their own subscription. The Head Coach is responsible for who they authorize and for removing access when a staff member leaves the team.
      </P>

      <H>Your content</H>
      <P>
        You own the coaching information you enter into CoachER — rosters, plans, lineups, and notes. You grant us a limited license to store, process, and display that content solely to provide the Service to you and the staff you authorize. You are responsible for the content you enter and for having the right to include player information.
      </P>

      <H>Acceptable use</H>
      <P>You agree not to:</P>
      <ul>
        <Li>Use the Service for anything unlawful or unrelated to its intended coaching purpose</Li>
        <Li>Access or attempt to access teams or accounts you are not authorized to join</Li>
        <Li>Interfere with, disrupt, or probe the security of the Service</Li>
        <Li>Scrape, copy, or resell the Service or its content</Li>
      </ul>

      <H>Our intellectual property</H>
      <P>
        The Service — including the CoachER name, logo, design, and software — is owned by {COMPANY.name} and protected by intellectual property laws. These Terms give you a limited, non-transferable right to use the Service; they do not transfer any ownership.
      </P>

      <H>Disclaimers</H>
      <P>
        The Service is provided "as is" and "as available." CoachER assists with organization and planning; coaching decisions — including lineup, playing-time, and player-safety decisions — remain yours. To the extent permitted by law, we disclaim all warranties, express or implied.
      </P>

      <H>Limitation of liability</H>
      <P>
        To the maximum extent permitted by law, {COMPANY.name} will not be liable for indirect, incidental, or consequential damages arising from your use of the Service, and our total liability will not exceed the amount you paid for the Service in the twelve months before the claim.
      </P>

      <H>Changes and termination</H>
      <P>
        We may update these Terms from time to time and will post the revised date above. Continued use after changes take effect means you accept them. We may suspend or terminate access for violations of these Terms. You may stop using the Service and delete your account at any time.
      </P>

      <H>Governing law</H>
      <P>These Terms are governed by the laws of the State of Ohio, USA, without regard to conflict-of-law rules.</P>

      <H>Contact</H>
      <P>
        Questions about these Terms? Email <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-coacher-navy underline decoration-coacher-red underline-offset-4">{CONTACT_EMAIL}</a>.
      </P>
    </Shell>
  );
};
